import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { format, addDays } from "date-fns";

const followUpTypes = [
  { value: "call", label: "Telefonsamtale" },
  { value: "meeting", label: "Møte" },
  { value: "email", label: "E-post" },
  { value: "check_in", label: "Check-in" },
  { value: "review", label: "Gjennomgang" },
];

const priorities = [
  { value: "low", label: "Lav" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "Høy" },
  { value: "urgent", label: "Haster" },
];

const getDisplayName = (user) => {
  if (user?.first_name || user?.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user?.full_name || user?.email || "";
};

export default function FollowUpForm({ followUp, investor, onSubmit, onClose, isLoading, currentUser }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "call",
    scheduled_date: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
    priority: "medium",
    reminder_date: format(addDays(new Date(), 1), "yyyy-MM-dd'T'09:00"),
    notes: "",
  });

  useEffect(() => {
    if (followUp) {
      setFormData({
        title: followUp.title || "",
        description: followUp.description || "",
        type: followUp.type || "call",
        scheduled_date: followUp.scheduled_date || format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
        priority: followUp.priority || "medium",
        reminder_date: followUp.reminder_date || format(addDays(new Date(), 1), "yyyy-MM-dd'T'09:00"),
        notes: followUp.notes || "",
      });
    }
  }, [followUp]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      investor_id: investor.id,
      investor_name: investor.name,
      assigned_to: currentUser?.email,
      assigned_to_name: getDisplayName(currentUser),
      status: followUp?.status || "scheduled",
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100 sticky top-0 bg-white">
          <h2 className="text-xl font-semibold text-slate-900">
            {followUp ? "Rediger Oppfølging" : "Ny Oppfølging"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="p-4 bg-lime-100 rounded-lg">
            <p className="text-sm text-zinc-900 font-medium">Investor: {investor?.name}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Tittel *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="f.eks. Kvartalsvis oppfølging"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {followUpTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Prioritet *</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorities.map(priority => (
                    <SelectItem key={priority.value} value={priority.value}>{priority.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="scheduled_date">Planlagt tidspunkt *</Label>
            <Input
              id="scheduled_date"
              type="datetime-local"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reminder_date">Påminnelsestidspunkt</Label>
            <Input
              id="reminder_date"
              type="datetime-local"
              value={formData.reminder_date}
              onChange={(e) => setFormData({ ...formData, reminder_date: e.target.value })}
            />
            <p className="text-xs text-slate-500">Du vil motta en e-postpåminnelse på dette tidspunktet</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Hva skal diskuteres?"
            />
          </div>

          {followUp && (
            <div className="space-y-2">
              <Label htmlFor="notes">Notater (etter oppfølging)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                placeholder="Sammendrag av samtalen..."
              />
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Avbryt</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {isLoading ? "Lagrer..." : followUp ? "Oppdater" : "Opprett Oppfølging"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}